import React, {
  ComponentPropsWithoutRef,
  ElementType,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useLocation } from 'react-use';
import { Link, useRouteMatch } from 'react-router-dom';

import {
  compilePath,
  ADD_FEATURE_TYPE,
  FEEDBACK_WIZARD_STEP,
} from '../../../../common/utils/helpers/routes';
import {
  selectLocation,
  selectDraggedState,
} from '../../../../common/store/item-edit';
import { selectGeolocation } from '../../../../map/store';
import { selectStyleSelectorShown } from '../../../../common/store/ui';
import {
  IconList,
  IconListItem,
} from '../../../../common/components/icon-list/IconList';
import WizardLayout from '../../../../common/components/wizard-layout/WizardLayout';
import { useMobile } from '../../../../common/components/responsive/Responsive';
import AddAddressForm from '../forms/add-address-form/AddAddressForm';
import PoiForm from '../../../../common/components/forms/poi-form/PoiForm';
import AddRoadForm from '../forms/add-road-form/AddRoadForm';
import AddSpeedLimitForm from '../forms/add-speed-limit-form/AddSpeedLimitForm';
import AddIncidentForm from '../forms/add-incident-form/AddIncidentForm';
import AddClearanceForm from '../forms/add-clearance-form/AddClearanceForm';

import './AddPlace.scss';

export interface AddPlaceProps extends ComponentPropsWithoutRef<ElementType> {
  className;
  onSubmit;
  onCancel;
}

interface RouteMatch {
  wizardStep;
  featureType;
}

const AddPlace = ({
  className,
  onSubmit,
  onCancel,
  ...props
}: AddPlaceProps) => {
  const match = useRouteMatch<RouteMatch>();
  const location = useLocation();
  const itemDragPosition = useSelector(selectLocation);
  const itemMarkerDragged = useSelector(selectDraggedState);
  const userLocation = useSelector(selectGeolocation);
  const isMobile = useMobile();

  const handleAddAddressClick = useCallback(
    () => analytics.track('Open Add Address', { from: 'sidebar' }),
    [],
  );

  const handleAddBusinessClick = useCallback(
    () => analytics.track('Open Add Business', { from: 'sidebar' }),
    [],
  );

  const handleAddRoadClick = useCallback(
    () => analytics.track('Open Add Road', { from: 'sidebar' }),
    [],
  );

  const handleAddSpeedLimitClick = useCallback(
    () => analytics.track('Open Add Speed limit', { from: 'sidebar' }),
    [],
  );

  const handleAddIncidentClick = useCallback(
    () => analytics.track('Open Add Incident', { from: 'sidebar' }),
    [],
  );

  const handleAddClearanceClick = useCallback(
    () => analytics.track('Open Add Clearance', { from: 'sidebar' }),
    [],
  );

  const isSelectAddressFromMapDisabled = useMemo(
    () => !itemDragPosition,
    [itemDragPosition],
  );

  const styleSelectorShown = useSelector(selectStyleSelectorShown);
  if (isMobile && styleSelectorShown) {
    return null;
  }

  let title;
  let description;
  let content;

  const { wizardStep, featureType } = match.params;

  switch (featureType) {
    case ADD_FEATURE_TYPE.address:
      title = 'Add Address';
      description = 'Set the location pin on the road';

      if (isMobile && wizardStep) {
        switch (wizardStep) {
          case FEEDBACK_WIZARD_STEP.feedback:
            description = 'Drag the map to edit the location';
            break;

          case FEEDBACK_WIZARD_STEP.additionalInformation:
            title = 'Add additional information';
            description = null;
            break;
        }
      }

      content = (
        <AddAddressForm
          userLocation={userLocation}
          itemDragPosition={itemDragPosition}
          itemMarkerDragged={itemMarkerDragged}
          formDisabled={isSelectAddressFromMapDisabled}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
      break;

    case ADD_FEATURE_TYPE.business:
      title = 'Add Business';
      description = 'Set the location pin on the road';

      if (isMobile && wizardStep) {
        switch (wizardStep) {
          case FEEDBACK_WIZARD_STEP.location:
            title = 'Set the exact location on map';
            description = 'Drag the map to edit the address';
            break;

          case FEEDBACK_WIZARD_STEP.feedback:
            description = null;
            break;

          case FEEDBACK_WIZARD_STEP.additionalInformation:
            title = 'Add additional information';
            description = null;
            break;
        }
      }

      content = (
        <PoiForm
          action="create"
          reason="missing_place"
          userLocation={userLocation}
          itemDragPosition={itemDragPosition}
          itemMarkerDragged={itemMarkerDragged}
          formDisabled={isSelectAddressFromMapDisabled}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
      break;

    case ADD_FEATURE_TYPE.road:
      title = 'Add Road';
      description = 'Set the location pin on the road';

      if (isMobile && wizardStep) {
        switch (wizardStep) {
          case FEEDBACK_WIZARD_STEP.location:
            title = 'Set the exact location on map';
            description = 'Drag the map to edit the location';
            break;

          case FEEDBACK_WIZARD_STEP.feedback:
            description = null;
            break;

          case FEEDBACK_WIZARD_STEP.additionalInformation:
            title = 'Add additional information';
            description = null;
            break;
        }
      }

      content = (
        <AddRoadForm
          userLocation={userLocation}
          itemDragPosition={itemDragPosition}
          formDisabled={isSelectAddressFromMapDisabled}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
      break;

    case ADD_FEATURE_TYPE.speedLimit:
      title = 'Add Speed limit';
      description = 'Set the location pin on the road';

      if (isMobile && wizardStep) {
        switch (wizardStep) {
          case FEEDBACK_WIZARD_STEP.feedback:
            description = 'Drag the map to edit the location';
            break;

          case FEEDBACK_WIZARD_STEP.additionalInformation:
            title = 'Add additional information';
            description = null;
            break;
        }
      }

      content = (
        <AddSpeedLimitForm
          userLocation={userLocation}
          itemDragPosition={itemDragPosition}
          formDisabled={isSelectAddressFromMapDisabled}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
      break;

    case ADD_FEATURE_TYPE.incident:
      title = 'Add Incident or Closure';
      description = 'Set the location pin on the road';

      if (isMobile && wizardStep) {
        switch (wizardStep) {
          case FEEDBACK_WIZARD_STEP.location:
            title = 'Set the exact location on map';
            description = 'Drag the map to edit the location';
            break;

          case FEEDBACK_WIZARD_STEP.feedback:
            description = null;
            break;

          case FEEDBACK_WIZARD_STEP.additionalInformation:
            title = 'Add additional information';
            description = null;
            break;
        }
      }

      content = (
        <AddIncidentForm
          userLocation={userLocation}
          itemDragPosition={itemDragPosition}
          formDisabled={isSelectAddressFromMapDisabled}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
      break;

    case ADD_FEATURE_TYPE.clearance:
      title = 'Add Clearance';
      description = 'Set the location pin on the road';

      if (isMobile && wizardStep) {
        switch (wizardStep) {
          case FEEDBACK_WIZARD_STEP.location:
            title = 'Set the exact location on map';
            description = 'Drag the map to edit the location';
            break;

          case FEEDBACK_WIZARD_STEP.feedback:
            description = null;
            break;

          case FEEDBACK_WIZARD_STEP.additionalInformation:
            title = 'Add additional information';
            description = null;
            break;
        }
      }

      content = (
        <AddClearanceForm
          userLocation={userLocation}
          itemDragPosition={itemDragPosition}
          formDisabled={isSelectAddressFromMapDisabled}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
      break;

    default:
      title = 'Add data to the map';
      content = (
        <IconList className="add-place__icon-list">
          <IconListItem
            icon="pin"
            component={Link}
            onClick={handleAddAddressClick}
            to={{
              pathname: compilePath('add', null, {
                featureType: ADD_FEATURE_TYPE.address,
              }),
              search: location.search,
            }}
          >
            Address
          </IconListItem>
          <IconListItem
            icon="building"
            component={Link}
            onClick={handleAddBusinessClick}
            to={{
              pathname: compilePath('add', null, {
                featureType: ADD_FEATURE_TYPE.business,
              }),
              search: location.search,
            }}
          >
            Business
          </IconListItem>
          <IconListItem
            icon="road"
            component={Link}
            onClick={handleAddRoadClick}
            to={{
              pathname: compilePath('add', null, {
                featureType: ADD_FEATURE_TYPE.road,
              }),
              search: location.search,
            }}
          >
            Road
          </IconListItem>
          <IconListItem
            icon="speed-limit"
            component={Link}
            onClick={handleAddSpeedLimitClick}
            to={{
              pathname: compilePath('add', null, {
                featureType: ADD_FEATURE_TYPE.speedLimit,
              }),
              search: location.search,
            }}
          >
            Speed limit
          </IconListItem>
          <IconListItem
            icon="incident"
            component={Link}
            onClick={handleAddIncidentClick}
            to={{
              pathname: compilePath('add', null, {
                featureType: ADD_FEATURE_TYPE.incident,
              }),
              search: location.search,
            }}
          >
            Incident / Closure
          </IconListItem>
          <IconListItem
            icon="clearance"
            component={Link}
            onClick={handleAddClearanceClick}
            to={{
              pathname: compilePath('add', null, {
                featureType: ADD_FEATURE_TYPE.clearance,
              }),
              search: location.search,
            }}
          >
            Clearance
          </IconListItem>
        </IconList>
      );
  }

  const shouldShowOuterBackButton =
    isMobile &&
    ((featureType === ADD_FEATURE_TYPE.business &&
      wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.road &&
        wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.speedLimit &&
        wizardStep === FEEDBACK_WIZARD_STEP.feedback) ||
      (featureType === ADD_FEATURE_TYPE.incident &&
        wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.clearance &&
        wizardStep === FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.address &&
        wizardStep === FEEDBACK_WIZARD_STEP.feedback));

  const isFullScreen =
    isMobile &&
    ((featureType === ADD_FEATURE_TYPE.address &&
      wizardStep !== FEEDBACK_WIZARD_STEP.feedback) ||
      (featureType === ADD_FEATURE_TYPE.business &&
        wizardStep !== FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.road &&
        wizardStep !== FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.incident &&
        wizardStep !== FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.clearance &&
        wizardStep !== FEEDBACK_WIZARD_STEP.location) ||
      (featureType === ADD_FEATURE_TYPE.speedLimit &&
        wizardStep !== FEEDBACK_WIZARD_STEP.feedback));

  const hasCloseButton =
    !isMobile ||
    (featureType === ADD_FEATURE_TYPE.address &&
      wizardStep === FEEDBACK_WIZARD_STEP.feedback) ||
    (featureType === ADD_FEATURE_TYPE.business &&
      wizardStep === FEEDBACK_WIZARD_STEP.location) ||
    (featureType === ADD_FEATURE_TYPE.road &&
      wizardStep === FEEDBACK_WIZARD_STEP.location) ||
    (featureType === ADD_FEATURE_TYPE.speedLimit &&
      wizardStep === FEEDBACK_WIZARD_STEP.feedback) ||
    (featureType === ADD_FEATURE_TYPE.incident &&
      wizardStep === FEEDBACK_WIZARD_STEP.location) ||
    (featureType === ADD_FEATURE_TYPE.clearance &&
      wizardStep === FEEDBACK_WIZARD_STEP.location);

  return (
    <WizardLayout
      className={classNames('add-place', className)}
      {...props}
      title={title}
      description={description}
      hasTitleBackButton={isFullScreen}
      hasOuterBackButton={shouldShowOuterBackButton}
      fullScreen={isFullScreen}
      hasCloseButton={hasCloseButton}
    >
      {content}
    </WizardLayout>
  );
};

export default AddPlace;
